import React, { useState, useContext, useEffect, useMemo } from "react"
import intl from "react-intl-universal"

import styles from "./index.module.scss"
import ConfigIcon from "../../../assets_v2/icons/ConfigIcon"
import NotificationIcon from "../../../assets_v2/icons/NotificationIcon"
import AuthContext from "../../../contexts/Auth"
import RcmpLink from "../../../components_v2/atoms/Link"
import { fnGetNewNotifications } from "../../../services/notifications"
import { fnBisAdmin } from "../../../utils/permissions"
import RcmpBreadCrumb from "../../atoms/BreadCrumb"
import RcmpModalInfo from "../../../components_v2/organisms/ModalInfo"
import { CustomTypography } from "../../../components_v2/atoms/CustomTypography"
import { fnGetStatusLogin } from "../../../services/auth"
import useDataApi from "../../../hooks/useDataApi"
import MenuHamburguerIcon from "../../../assets_v2/icons/MenuHamburguerIcon"
import SideBarContext from "../../../contexts/SideBar"
import {
  fnDecodeStorageToken,
} from "../../../utils/encodeLocalStorage"
import ArrowDownIcon from "../../../assets_v2/icons/ArrowDownIcon"
import HelpIcon from "../../../assets_v2/icons/HelpIcon"
import DocumentIcon from "../../../assets_v2/icons/DocumentIcon"
import VideoIcon from "../../../assets_v2/icons/VideoIcon"
import WhatsAppIcon from "../../../assets_v2/icons/WhatsAppIcon"
import NotificationAlertIcon from "../../../assets_v2/icons/NotificationAlertIcon"
import RcmpFloatingMenu from "../../atoms/FloatingMenu"
import { RcmpUser } from "../../atoms/CardUsers"
import { useLocation } from "react-router-dom"
import { useAppearanceContext } from "../../../contexts/Appearance"
import { signIn } from "../../../config/Authentication"

function RcmpHeaderBreadCrumb(props) {
  const { aBreadCrumbs, className } = props;

  if (!aBreadCrumbs?.length) return null;

  const cClass = `${styles.breadCrumbAlignment} ${className || ""}`;

  const aBreadCrumbsWithHome = [
    { name: intl.get("home") || "Início", path: "/", bActive: false },
    ...aBreadCrumbs,
  ];

  return (
    <RcmpBreadCrumb className={cClass}>
      {aBreadCrumbsWithHome.map(({ bActive, name, path }, nIndex) =>
        path === "/" ? (
          <RcmpBreadCrumb.RcmpItem
            key="home"
            as="a"
            href="/"
            bActive={false}
          >
            {intl.get("home")}
          </RcmpBreadCrumb.RcmpItem>
        ) : (
          <RcmpBreadCrumb.RcmpItem key={nIndex} bActive={bActive} to={path}>
            {name}
          </RcmpBreadCrumb.RcmpItem>
        )
      )}
    </RcmpBreadCrumb>
  );
}

function RcmpHeaderHelpMenu() {
  const [bIsMenuOpen, fnSetBIsMenuOpen] = useState(false)

  const message = 'Olá! Preciso de ajuda.';
  const phone = "5543991948972";
  const num = String(phone || '').replace(/\D+/g, '');

  const aHelpOptions = [
    {
      path: "https://manual.acredite.se/",
      name: intl.get("header.manual"),
      icon: <DocumentIcon stroke="currentColor" />,
      target: "_blank",
      rel: "noopener noreferrer",
    },
    {
      path: "https://www.youtube.com/playlist?list=PL3g_0v4YkMJdpGvBf-fQ4IcCFIbjqLL22",
      name: intl.get("header.training"),
      icon: <VideoIcon stroke="currentColor" />,
      target: "_blank",
      rel: "noopener noreferrer",
    },
    {
      path: `https://api.whatsapp.com/send?phone=${num}&text=${encodeURIComponent(message)}`,
      name: intl.get("header.help"),
      icon: <WhatsAppIcon />,
    },
  ]

  return (
    <RcmpFloatingMenu
      aOptions={aHelpOptions}
      fnOnChangeIsOpen={fnSetBIsMenuOpen}
    >
      <div className={styles.helpButton}>
        <span className={styles.helpButtonIcon}>
          <HelpIcon />
        </span>
        <span className={styles.helpButtonLabel}>
          {intl.get("header.help-label")}
        </span>
      </div>
    </RcmpFloatingMenu>
  )
}

function RcmpHeaderConfigMenu() {
  const { oUser } = useContext(AuthContext)
  const { oConfig: oConfigWL } = useAppearanceContext()
  const location = useLocation()

  const [bIsMenuOpen, fnSetBIsMenuOpen] = useState(false)

  if (!fnBisAdmin()) return null

  const aCustInfo = useMemo(() => {
    const aResults = []

    const { customer } = oUser?.data || {}
    if (customer) {
      const cCustName = customer.cust_cfantasyname || customer.cust_cname || ""
      aResults.push(cCustName)

      const cCustDoc = customer.cust_ccnpj || customer.cust_ccpf
      aResults.push(`${cCustDoc} | ID: ${customer.cust_nid}`);
    }

    return aResults
  }, [oUser])

  const cVersion = process.env.REACT_APP_VERSION

  const aConfigOptions = [
    {
      path: "./customer/update/",
      name: intl.get("header.costumer-update"),
    },
    {
      path: "./plan",
      name: intl.get("header.plans"),
    },
    {
      path: "/document/configure",
      name: intl.get("header.config-footer"),
      state: { returnTo: `${location.pathname}${location.search}` },
    },
  ]

  if (!oConfigWL || !oConfigWL.nWlpnId) {
    aConfigOptions.push(
      {
        path: "./extract",
        name: intl.get("header.extract"),
      },
      {
        path: "./terms",
        name: intl.get("header.terms"),
      },
    )
  }

  return (
    <RcmpFloatingMenu
      aOptions={aConfigOptions}
      fnOnChangeIsOpen={fnSetBIsMenuOpen}
      elFooter={
        <div className={styles.configMenuFooter}>
          {aCustInfo.map((item) => (
            <p
              key={item}
              style={{ textOverflow: "ellipsis", maxWidth: "100%", overflow: "hidden" }}
              title={item}
            >
              {item}
            </p>
          ))}
          {intl.get("header.version", {
            version: cVersion?.replace("v", ""),
          })}
        </div>
      }
    >
      <div
        className={`${!bIsMenuOpen ? styles.configIcon : styles.configIconOpen} ${styles.alignIcon} ${styles.iconHover}`}
      >
        <ConfigIcon />
      </div>
    </RcmpFloatingMenu>
  )
}

function RcmpHeaderUserMenu(props) {
  const { bNewNotification } = props
  const { oUser, fnLogout } = useContext(AuthContext)
  const [bIsMenuOpen, fnSetBIsMenuOpen] = useState(false)

  const aUserOptions = [
    {
      className: styles.hideDesktop,
      path: "./notification",
      name: (
        <span className={styles.menuNewNotification}>
          {intl.get("header.notifications")} {bNewNotification && <span />}
        </span>
      ),
    },
    {
      path: "./quality-panel-consultant/?initial=1",
      name: intl.get("sidemenu.quality-panel-consultant"),
    },
  ]

  if (fnDecodeStorageToken()?.bInternalUser === false) {
    aUserOptions.push({
      path: "./my-account/my-profile",
      name: intl.get("sidemenu.my-profile"),
    })
  }
  return (
    <RcmpFloatingMenu
      aOptions={aUserOptions}
      fnOnChangeIsOpen={fnSetBIsMenuOpen}
      elFooter={
        <div className={styles.userMenuFooter}>
          <a href="#" onClick={fnLogout}>
            {intl.get("sidemenu.logout")}
          </a>
        </div>
      }
      className={styles.userMenu}
    >
      <figure className={`${styles.avatar} ${styles.hideMobile}`}>
        <RcmpUser
          oUser={oUser?.data}
          bDisabledTooltip={true}
          className={styles.userAvatar}
        />
        <div className={styles.userMenuArrow}>
          <label className={styles.userName}>{oUser?.data?.customer?.cust_cfantasyname}</label>
          <div
            className={`${styles.userMenuArrow} ${bIsMenuOpen ? styles.userMenuArrowUp : ""}`}
          >

            <ArrowDownIcon />
          </div>
        </div>
      </figure>

      <div className={`${styles.mobileUserMenu} ${styles.hideDesktop}`}>
        <div>
          <RcmpUser oUser={oUser?.data} bDisabledTooltip={true} />
          {bNewNotification && oUser?.data && (
            <div className={styles.userNewNotificationDot}></div>
          )}
        </div>
      </div>
    </RcmpFloatingMenu>
  )
}

function RcmpHeader(props) {
  const {
    children,
    cTitlePage,
    elIcon,
    aBreadCrumbs,
    className,
    bHasBorder = true,
    ...oRest
  } = props

  const [bNewNotification, fnSetBNewNotification] = useState(false)
  const [bTrialExpired, fnSetBTrialExpired] = useState(false)
  const [bIsNewLayout, fnSetBIsNewLayout] = useState(false)

  const [oGetStatusLoginRes, fnGetStatusLoginReq] = useDataApi(fnGetStatusLogin)

  const { fnLogout, oUser } = useContext(AuthContext)
  const { fnSetBSideBarIsOpen, fnSetBSideBarOnAfterClose } = useContext(SideBarContext)
  const { isWL } = useAppearanceContext()

  const location = useLocation()

  useEffect(() => {
    if (oUser?.data?.bIsNewLayout && !location.pathname.startsWith("/ciq")) {
      fnSetBIsNewLayout(true)
      return
    }

    fnSetBIsNewLayout(false)
  }, [location.pathname, oUser?.data?.bIsNewLayout])

  function fnOpenSideBar() {
    fnSetBSideBarIsOpen(true)
    fnSetBSideBarOnAfterClose(false)
  }

  async function fnGetNewNotitificationsWithParams(bIsAutomatic) {
    const response = await fnGetNewNotifications(
      bIsAutomatic ? "bNotRenewing=false" : null,
    )
    fnSetBNewNotification(response?.data)
  }

  useEffect(() => {
    let tmInterval
      ; (async () => {
        await fnGetStatusLoginReq()
        await fnGetNewNotitificationsWithParams()

        tmInterval = setInterval(async () => {
          await fnGetNewNotitificationsWithParams(true)
        }, 60000)
      })()

    return () => {
      clearInterval(tmInterval)
    }
  }, [])

  useEffect(() => {
    if (!oGetStatusLoginRes.done) return

    const cTokenNew = oGetStatusLoginRes?.data
    if (cTokenNew) {
      signIn(cTokenNew)
    }

    const oToken = fnDecodeStorageToken() ?? {}

    if (!oToken.nCustId && window.location.pathname !== "/quality-panel-consultant") {
      window.location.href = "/quality-panel-consultant"
    }

    if (
      !oToken.bActive &&
      !["/plan", "/extract", "/quality-panel-consultant"].includes(
        window.location.pathname,
      )
    ) {
      window.location.href = !oToken.bTrial && !isWL ? "/extract" : "/plan"
    }

    fnSetBTrialExpired(oGetStatusLoginRes.error?.request?.status === 403)
  }, [oGetStatusLoginRes])

  const cClass = `${styles.headerContainer} ${bIsNewLayout ? styles.headerNewLayout : ""} ${className ? className : ""}`

  return (
    <div className={cClass} {...oRest}>
      <div
        className={styles.headerContent}
        style={{ borderBottom: bHasBorder ? "1px solid #ccd1d6" : "none" }}
      >
        <div className={styles.menuHamburguerMobile} onClick={fnOpenSideBar}>
          <MenuHamburguerIcon />
        </div>

        {/* Conteúdo do título + breadcrumb */}
        <div>
          {cTitlePage && (
            <div className={styles.titlePage}>
              <div className={styles.iconTitle}>
                {elIcon && React.cloneElement(elIcon)}
              </div>
              <CustomTypography>{cTitlePage}</CustomTypography>
            </div>
          )}

          {aBreadCrumbs?.length > 0 && (
            <div className={styles.breadCrumbArea}>
              <RcmpHeaderBreadCrumb aBreadCrumbs={aBreadCrumbs} />
            </div>
          )}
        </div>

        <div className={styles.headerRight}>
          <div className={styles.headerMenu}>
            <RcmpHeaderHelpMenu />
            <RcmpHeaderConfigMenu />

            <RcmpLink to="./notification" className={styles.hideMobile}>
              {bNewNotification ? (
                <div
                  className={`${styles.notificationAlertIcon} ${styles.alignIcon} ${styles.iconHover}`}
                >
                  <NotificationAlertIcon />
                </div>
              ) : (
                <div
                  className={`${styles.notNotificationAlertIcon} ${styles.alignIcon} ${styles.iconHover}`}
                >
                  <NotificationIcon />
                </div>
              )}
            </RcmpLink>

            <RcmpHeaderUserMenu bNewNotification={bNewNotification} />
          </div>
        </div>
      </div>

      {/* Removido: Breadcrumb fora do headerContent */}

      {React.Children.count(children) > 0 && (
        <div className={styles.headerChildren}>{children}</div>
      )}

      <RcmpModalInfo
        bIsOpen={bTrialExpired}
        title="FIM DO PERÍODO DE TRIAL"
        description="O tempo de avaliação do sistema chegou ao fim. Para continuar a utilizar o produto entre em contato com seu administrador."
        size="sm"
        buttonSaveText="OK"
        oButtonSaveProps={{ style: { margin: "0 auto" } }}
        oButtonCancelProps={{ style: { display: "none" } }}
        fnHandleConfirm={fnLogout}
      />
    </div>
  )

}

export default RcmpHeader
